import { RefreshCw, Calendar, Download } from 'lucide-react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { exportBudgetDashboard } from '../../utils/excelExport';

function DashboardHeader({ onRefresh, loading, filters, dashboardData }) {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isExporting, setIsExporting] = useState(false);
  
  const userEmail = isAuthenticated && accounts.length > 0 ? accounts[0].username : null;
  const userName = isAuthenticated && accounts.length > 0 ? accounts[0].name : 'User';

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleExport = async () => {
    try {
      setIsExporting(true);
      toast.info('Preparing Excel export...', { autoClose: 2000 });
      
      // Use the backend API for export
      const result = await exportBudgetDashboard(filters || {});
      
      if (result.success) {
        toast.success('Excel file downloaded successfully!', { autoClose: 3000 });
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message}`, { autoClose: 5000 });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div 
      className="border-b px-6 py-4"
      style={{ 
        background: 'var(--panel)', 
        borderColor: 'var(--border)' 
      }}
    >
      <div className="w-full flex items-center justify-between">
        {/* Left side - Title and date */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Budget Dashboard
          </h1>
          <div className="flex items-center gap-4 mt-1">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" style={{ color: 'var(--muted)' }} />
              <span className="text-sm" style={{ color: 'var(--muted)' }}>
                {currentDate}
              </span>
            </div>
            {userEmail && (
              <div className="text-sm" style={{ color: 'var(--muted)' }}>
                Welcome back, {userName.split(' ')[0]}
              </div>
            )}
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-3">
          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors"
            style={{ 
              background: loading ? 'var(--border)' : 'transparent',
              borderColor: 'var(--border)',
              color: loading ? 'var(--muted)' : 'var(--text)'
            }}
            title="Refresh dashboard data"
          >
            <RefreshCw 
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} 
            />
            <span className="text-sm font-medium">
              {loading ? 'Refreshing...' : 'Refresh'}
            </span>
          </button>

          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={isExporting || loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-semibold"
            style={{ 
              background: isExporting ? 'var(--border)' : '#8c211e',
              color: isExporting ? 'var(--muted)' : 'white',
              opacity: isExporting || loading ? 0.7 : 1
            }}
            title="Export dashboard data to Excel"
          >
            <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
            <span className="text-sm font-semibold">
              {isExporting ? 'Exporting...' : 'Export'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default DashboardHeader;